/**
 * "เปิดแชทนี้ใน Meta Business Suite" (owner 27 ก.ย. 69 — the old link opened the Page's newest chat, not this
 * customer's). Business Suite selects a chat only by `selected_item_id`, which is none of the ids the Graph API gives,
 * so staff paste the chat's Business Suite URL once (api/app/meta_chat.py). Same checks as the server, so a wrong
 * paste is told at once; the server keeps only asset_id / mailbox_id / selected_item_id / thread_type.
 */

export const BAD_META_URL =
  'ลิงก์ไม่ถูกต้อง · เปิดแชทของลูกค้าคนนี้ใน Meta Business Suite (business.facebook.com) แล้วคัดลอกลิงก์จากช่องที่อยู่ของเบราว์เซอร์'
export const META_NO_CHAT = 'ลิงก์นี้ยังไม่ได้เลือกแชท · คลิกแชทของลูกค้าคนนี้ใน Meta Business Suite ก่อน แล้วคัดลอกลิงก์อีกครั้ง'

const NUM = /^[0-9]{3,30}$/
const PATH = /^\/latest\/inbox\/([a-z_]{1,40})\/?$/
const THREAD_TYPE = /^[A-Z_]{1,30}$/

export interface MetaChatParts {
  asset: string
  item: string
  mailbox: string | null
  threadType: string | null
  path: string
}

/** A Business Suite chat URL → its parts, or the Thai reason it is refused. */
export function parseMetaChatUrl(text: string): MetaChatParts | string {
  const t = text.trim()
  if (!t || t.length > 2000 || /[\s\u0000-\u001f]/.test(t)) return BAD_META_URL
  let u: URL
  try {
    u = new URL(t)
  } catch {
    return BAD_META_URL
  }
  if (u.protocol !== 'https:' || u.host.toLowerCase() !== 'business.facebook.com' || u.username || u.password) return BAD_META_URL
  const m = PATH.exec(u.pathname)
  if (!m) return BAD_META_URL
  const one = (k: string): string | null | undefined => {
    const all = u.searchParams.getAll(k).filter((v) => v !== '')
    return all.length > 1 ? undefined : (all[0] ?? null)
  }
  const asset = one('asset_id')
  const item = one('selected_item_id')
  const mailbox = one('mailbox_id')
  const threadType = one('thread_type')
  if ([asset, item, mailbox, threadType].includes(undefined)) return BAD_META_URL
  if (!asset || !NUM.test(asset) || (mailbox && !NUM.test(mailbox)) || (threadType && !THREAD_TYPE.test(threadType))) return BAD_META_URL
  if (!item) return META_NO_CHAT
  if (!NUM.test(item)) return BAD_META_URL
  return { asset, item, mailbox: mailbox ?? null, threadType: threadType ?? null, path: m[1]! }
}

/** Thai reason a pasted Business Suite link is refused before asking the server (null = looks right). */
export function metaChatUrlProblem(text: string): string | null {
  const got = parseMetaChatUrl(text)
  return typeof got === 'string' ? got : null
}

// ---------------------------------------------------------------- Business ID (owner setting, 27 ก.ย. 69)

/** Same text as api/app/meta_chat.py BAD_BUSINESS_ID. */
export const BAD_BUSINESS_ID = 'Business ID ต้องเป็นตัวเลขเท่านั้น (5–25 หลัก) · ดูได้จากลิงก์ Meta Business Suite ตรง business_id='
const BUSINESS_ID = /^[0-9]{5,25}$/

/**
 * What the owner typed or pasted → the Business ID digits · '' = leave it out of the links · null = not valid.
 * A Business Suite URL works too (its `business_id=` is taken): the server itself only accepts digits.
 */
export function parseBusinessId(text: string): string | null {
  const t = text.trim()
  if (!t) return ''
  if (BUSINESS_ID.test(t)) return t
  try {
    const u = new URL(t)
    if (u.protocol === 'https:' && u.host.toLowerCase() === 'business.facebook.com') {
      const id = u.searchParams.get('business_id') ?? ''
      return BUSINESS_ID.test(id) ? id : null
    }
  } catch {
    /* not a URL */
  }
  return null
}
